import React from 'react'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import { Grid } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import * as R from 'ramda'
import { Text } from '@pbt/pbt-ui-components'
import { Check, Exclamation } from '@pbt/pbt-ui-components/src/icons'

import { BodySystemState } from '~/constants/SOAPStates'
import { ProblemDiagnosisLog } from '~/types'

import ProblemsDiagnosesTableBodySystem from './ProblemsDiagnosesTableBodySystem'

const useStyles = makeStyles(
  (theme) => ({
    root: {
      border: theme.constants.tableBorder,
    },
    icon: {
      width: theme.spacing(2),
      height: theme.spacing(2),
      borderRadius: theme.spacing(1),
      backgroundColor: theme.colors.success,
      color: theme.colors.tableBackground,
      marginRight: theme.spacing(1),
      verticalAlign: 'text-top',
      padding: 1,
    },
    infoIcon: {
      backgroundColor: theme.colors.alertError,
    },
    closeIcon: {
      backgroundColor: theme.colors.filterBorderColor,
      color: theme.colors.tableBackground,
    },
  }),
  { name: 'ProblemsDiagnosesTable' },
)

interface ProblemsDiagnosesTableProps {
  problemFindingLogs: ProblemDiagnosisLog[]
  state?: BodySystemState
}

const ProblemsDiagnosesTable = ({
  problemFindingLogs,
  state = BodySystemState.ONL,
}: ProblemsDiagnosesTableProps) => {
  const classes = useStyles()
  const { t } = useTranslation('Soap')

  const bodySystemsIds = R.uniq(problemFindingLogs.map(R.prop('bodySystemId')))

  const icons = {
    [BodySystemState.ONL]: (
      <Exclamation className={classNames(classes.icon, classes.infoIcon)} />
    ),
    [BodySystemState.WNL]: <Check className={classes.icon} />,
    [BodySystemState.NC]: (
      <CloseIcon className={classNames(classes.icon, classes.closeIcon)} />
    ),
  }

  const headerText = {
    [BodySystemState.ONL]: t('Soap:FINDINGS_TABLE.OUTSIDE_NORMAL_LIMITS_TITLE'),
    [BodySystemState.WNL]: t(
      'Soap:FINDINGS_TABLE.WITHIN_NORMAL_LIMITS_FINDING_TITLE',
    ),
    [BodySystemState.NC]: t('Soap:FINDINGS_TABLE.NOT_CHECKED_TITLE'),
  }

  return (
    <Grid
      container
      item
      className={classes.root}
      direction="column"
      display="flex"
    >
      <Grid container item p={1} xs={12}>
        <Text strong variant="body">
          {icons[state]}
          {headerText[state]}
        </Text>
      </Grid>
      {bodySystemsIds.map((bodySystemId: string) => (
        <ProblemsDiagnosesTableBodySystem
          bodySystemId={bodySystemId}
          key={bodySystemId}
          logs={problemFindingLogs.filter(
            (log) => log.bodySystemId === bodySystemId,
          )}
        />
      ))}
    </Grid>
  )
}
export default ProblemsDiagnosesTable
